/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { EditorContent, EmbedCode } from './editor-content.model';

/** Defines the emitter data of media-upload component on URL input */
export interface MediaUrlData extends EditorContent {
  /** The URL a user inputs into the media-upload URL input. */
  contentUrl: string;
  /** The embedable html content generated from the {@link contentUrl}. */
  contentHtml: string;
  /** The position/float of the media. */
  position?: ImagePosition;
  /** The type of the media. */
  mediaType: MediaType;
  /** The title of the resource. */
  title?: string;
  /** Url of the media/image. */
  url?: string;
  /** Should the media link open in a new tab. */
  openUrlInTab?: boolean;
}

/** Defines the emitter data of media-upload component on file selection */
export interface MediaUploadData extends EditorContent {
  uuid4?: string;
  /** The media name(the name of the file uploaded by the user), submitted to the API on media upload. */
  name?: string;
  /** The real file name of uploaded media */
  realName?: string;
  contentLength?: number;
  /** The URL to the uploaded resource, after the upload is complete, used for viewing the resource. */
  content?: MediaUploadContent;
  /** The File a user selects to upload. */
  file?: File;
  /** Defines if the upload is the main one for the section that it is within. */
  main?: boolean;
  /** The position/float of the media. */
  position?: ImagePosition;
  /** The exact alignment used in the editor (e.g. thumbnail-left/right). */
  alignment?: ImagePositionEnum | string;
  /** The type of the media. */
  mediaType: MediaType;
  /** Description of the media/image. */
  caption?: string;
  /** Url of the media/image. */
  url?: string;
  /** Should the media link open in a new tab. */
  openUrlInTab?: boolean;
  /** Should define if we are replacing media. */
  replace?: boolean;
  fileName?: string;
  /** How the media is presented to the learner: inline embed (default) or a download card. */
  displayMode?: MediaDisplayMode;
}

/** Defines how an uploaded media is presented to the learner. */
export enum MediaDisplayMode {
  /** The media is rendered inline (image, audio player, document viewer). */
  EMBED = 'EMBED',
  /** The media is rendered as a compact card with a download action. */
  DOWNLOAD = 'DOWNLOAD',
}

export type MediaType = 'IMAGE' | 'VIDEO' | 'STREAM_VIDEO' | 'DOCUMENT' | 'AUDIO' | 'ZIP';

export enum ImagePositionEnum {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
  CENTER = 'CENTER',
  FULL_WIDTH = 'FULL_WIDTH',
  THUMBNAIL = 'THUMBNAIL',
}

export type ImagePosition = `${ImagePositionEnum}`;

export type MediaUploadContentType = 'SINGLE_URL_CONTENT' | 'IMAGE_UPLOAD_CONTENT' | 'STREAM_VIDEO_CONTENT_DETAILS';

export interface MediaUploadContent {
  type: MediaUploadContentType;
}

/** Contains the url for the MediaUploadData. */
export interface SingleUrlContent extends MediaUploadContent {
  type: MediaUploadContentType;
  url: string;
}

/** Contains the URLs to an uploaded image with different dimensions. */
export interface ImageUploadContent extends MediaUploadContent {
  type: MediaUploadContentType;
  /** The original image URL. */
  original: string;
  /** The URL to the image with small dimensions.(230x200 px) */
  small: string;
  /** The URL to the image with width 400px. */
  medium: string;
  /** The URL to the image with width 830px. */
  large: string;
  /** The URL to the image with width 930px. */
  extraLarge: string;
}

/** Contains the details for Stream Videos. */
export interface StreamVideoContent extends MediaUploadContent {
  type: MediaUploadContentType;
  url: string;
  posterUrl: string;
}

/** Defines the emitter data of medium-editor on content change */
export interface MediumEditorData extends EditorContent {
  /** The content generated by medium-editor directive */
  content: string;
}

/** A summary of the main content extracted from an external source URL. */
export interface ExternalContentSummary extends EditorContent {
  /** The source URL. */
  url: string;
  /** The name of the resource owning organization/person. */
  authorName: string;
  /** The URL to the logo of the resource. */
  logoUrl: string;
  /** The resource title. */
  title: string;
  /** A short description of the resource. */
  description?: string;
  /** The resource publication date. */
  publicationDate: Date;
  /** The URL of the main thumbnail for the resource. */
  thumbnailUrl?: string;
  /** The name of the website. */
  siteName: string;
  /** Iframe code for embeddings. */
  html: string;

  position: ImagePosition;
}

/** Used as unification for MediaUrlData and MediaUploadData. */
export type MediaContent = MediaUrlData | MediaUploadData | MediumEditorData | ExternalContentSummary | EmbedCode;

/** Defines the emitter data of editor-toolbar */
export interface MediumEditorToolbarEvent {
  type: MediumEditorToolbarEventType;
  offsetTop?: number;
  element?: Element;
  contentUid?: string;
}

/** Defines the available types for MediumEditorToolbarEvent */
type MediumEditorToolbarEventType = 'SHOW_TOOLBAR' | 'HIDE_TOOLBAR';

export const CENTER_AND_FULL_WIDTH_TYPES = ['CENTER', 'FULL_WIDTH'];
